import React from "react";
import { motion } from "framer-motion";
import { Linkedin, Mail, MapPin, ArrowUp } from "lucide-react";
import { Link } from "react-scroll";
import { useTranslation } from "react-i18next";
// @ts-ignore
import cv from "../assets/cv.pdf";

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const locations = [
    { city: "Madrid", country: "Spain", icon: "🇪🇸" },
    { city: "London", country: "England", icon: "🇬🇧" },
    { city: "San Francisco", country: "California", icon: "🇺🇸" },
  ];

  const socialLinks = [
    // {
    //   name: "GitHub",
    //   href: "https://github.com/jamesbell",
    //   icon: Github,
    //   color: "hover:text-gray-300",
    // },
    {
      name: "LinkedIn",
      href: "https://linkedin.com/in/jamesbell",
      icon: Linkedin,
      color: "hover:text-blue-400",
    },
    {
      name: "Email",
      href: "mailto:james@example.com",
      icon: Mail,
      color: "hover:text-green-400",
    },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <footer className="relative bg-slate-900/50 backdrop-blur-lg border-t border-white/10">
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

      <div className="relative z-10 section-padding container-width py-16">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="grid lg:grid-cols-3 gap-12 ">
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Available Locations
              </h4>
              <div className="space-y-3">
                {locations.map((location) => (
                  <motion.div
                    key={location.city}
                    className="flex items-center space-x-3 text-gray-400"
                    whileHover={{ scale: 1.05, x: 10 }}
                    transition={{
                      type: "spring",
                      stiffness: 400,
                      damping: 17,
                    }}
                  >
                    <span className="text-xl">{location.icon}</span>
                    <span>
                      {location.city}, {location.country}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div variants={itemVariants}>
              <h4 className="text-lg font-semibold text-white mb-6">
                Quick Links
              </h4>
              <div className="space-y-3">
                {[
                  { name: t("header.about"), to: "about" },
                  { name: t("header.work"), to: "work" },
                  { name: t("header.projects"), to: "projects" },
                ].map((item) => (
                  <motion.div key={item.to} whileHover={{ x: 10 }}>
                    <Link
                      to={item.to}
                      smooth={true}
                      duration={500}
                      offset={-80}
                      className="text-gray-400 hover:text-white cursor-pointer transition-colors duration-200 block"
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
                <motion.div whileHover={{ x: 10 }}>
                  <a
                    href={cv}
                    download="James-Bell-CV.pdf"
                    className="text-gray-400 hover:text-white cursor-pointer transition-colors duration-200 block"
                  >
                    {t("header.downloadCv")}
                  </a>
                </motion.div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <h4 className="text-lg font-semibold text-white mb-6">
                Connect With Me
              </h4>
              <div className="space-y-4">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center space-x-3 text-gray-400 ${social.color} transition-all duration-200 group`}
                    whileHover={{ scale: 1.05, x: 10 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-white/20 transition-colors">
                      <social.icon className="w-4 h-4" />
                    </div>
                    <span>{social.name}</span>
                  </motion.a>
                ))}
              </div>

              <motion.button
                onClick={scrollToTop}
                className="mt-8 flex items-center space-x-2 text-gray-400 hover:text-white transition-colors duration-200 group"
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <ArrowUp className="w-4 h-4" />
                </div>
                <span>Back to Top</span>
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
    </footer>
  );
};

export default Footer;
